import React from "react";
import useDataAssetList from "../../hooks/useDataAssetList";
import PaginationFooter from "../paginationFooter";
import prettyBytes from "pretty-bytes";
import DataAssetCard from "../cards/dataAssetCard";
import SortDropdown from "../dropdowns/sortDropdown";
import SmartFilterChip from "../filterChip/smartFilterChip";
import FileTypeDropdown from "../dropdowns/fileTypeDropdown";
import useApiClient from "../../hooks/useApiClient";
import FilterChipList from "../filterChip/filterChipList";
import SpecifySearchTypeDropdown from "../dropdowns/specifySearchTypeDropdown";
import DeploymentTreeDropdown from "../realAsset/deploymentTreeDropdown";
import LoadingDataAssetCards from "../loaders/loadingDataAssetCards";
import Skeleton from "../loaders/Skeleton";
import DataAssetPreviewDialog from "./dataAssetPreviewDialog";
import { MINIMUM_SEARCH_TOKEN_LENGTH } from "@/utils/searchQueryValidation";

const RealAssetDeploymentDetailDataAssetList = ({
  shouldGrabFocus = false,
  deployment,
}: {
  shouldGrabFocus?: boolean;
  deployment: any;
}) => {
  const api = useApiClient();

  const {
    rawValues,
    setRawValues,
    queryParams,
    handleRealAssetSelection,
    handleDeploymentSelection,
    handleFileTypesSelection,
    handleSortChange,
    handleSpecifiedSearchTypeChange,
    specifiedSearchType,
    searchIsRunnable,
    searchQueryTooShort,
    isLoading,
    data,
    isPreviousData,
    searchStatsAreLoading,
    searchStatsData,
    setDebouncedQueryParams,
    previewNavigator,
  } = useDataAssetList(
    shouldGrabFocus,
    [],
    ["sealog", "advanced", "manifest", "dataSet"]
  );

  const filterChips = [
    {
      title: "File Type",
      items: rawValues.fileTypes,
      maxVisible: 3,
      onClose: handleFileTypesSelection,
    },
    {
      title: "Real Assets",
      items: rawValues.realAssetPublicIds,
      maxVisible: 4,
      onClose: handleRealAssetSelection,
      renderChip: (publicId: string) => (
        <SmartFilterChip
          key={publicId}
          title="Camera"
          selection={publicId}
          queryKey={["realAssetDetail", publicId]}
          onClose={() => handleRealAssetSelection(publicId)}
          mapQueryResponseToFilterChipLabel={(response: any) =>
            response?.name ?? ""
          }
          queryFn={() => api.dataAccess.realAssetDetail({ publicId })}
        />
      ),
    },
    {
      title: "Deployments",
      items: rawValues.realAssetDeploymentPublicIds,
      maxVisible: 4,
      onClose: handleDeploymentSelection,
      renderChip: (publicId: string) => (
        <SmartFilterChip
          key={publicId}
          title="Deployment"
          selection={publicId}
          queryKey={["realAssetDeploymentDetail", publicId]}
          onClose={() => handleDeploymentSelection(publicId)}
          mapQueryResponseToFilterChipLabel={(response: any) =>
            response?.name ?? ""
          }
          queryFn={() => api.dataAccess.realAssetDeploymentDetail({ publicId })}
        />
      ),
    },
  ];

  return (
    <>
      <DataAssetPreviewDialog
        isOpen={previewNavigator.isPreviewOpen}
        dataAsset={previewNavigator.selectedAsset}
        initialPreviewUrl={previewNavigator.initialPreviewUrl}
        onClose={previewNavigator.closePreview}
        onPrevious={previewNavigator.goToPreviousPreview}
        onNext={previewNavigator.goToNextPreview}
        hasPrevious={previewNavigator.hasPrevious}
        hasNext={previewNavigator.hasNext}
        navigatingDirection={previewNavigator.navigatingDirection}
        previousUnavailableMessage={previewNavigator.previousUnavailableMessage}
        nextUnavailableMessage={previewNavigator.nextUnavailableMessage}
        sealogSearchTerm={queryParams.sealogEventQuery ?? ""}
        sealogEventValues={queryParams.sealogEventValues}
        sealogEventFreeTexts={queryParams.sealogEventFreeTexts}
        sealogEventOptionNames={queryParams.sealogEventOptionNames}
        sealogEventOptionValues={queryParams.sealogEventOptionValues}
        regex={queryParams.regex}
      />
      <div className="max-w-[1500px]">
        <>
          <div className="space-y-4">
            <p className="mb-1 w-full text-3xl text-blue-100">Data Assets</p>
            <div className="flex justify-between">
              <div className="ml-[2px] mt-1 text-sm text-grey-200">
                {searchIsRunnable &&
                  (searchStatsAreLoading ? (
                    <Skeleton className="mb-3 h-4 w-48" />
                  ) : (
                    <p>
                      Number of Results:{" "}
                      {searchStatsData!.number_of_assets.toLocaleString()} |
                      Total File Size:{" "}
                      {prettyBytes(searchStatsData!.total_data_volume)}
                    </p>
                  ))}
              </div>
              <div className="flex space-x-3">
                <FileTypeDropdown
                  dropdownProps={{
                    selections: rawValues.fileTypes,
                    onOptionToggle: handleFileTypesSelection,
                    className: "w-[10rem]",
                  }}
                />
                <div className="z-50">
                  {deployment && (
                    <DeploymentTreeDropdown
                      deployment={deployment}
                      onOptionToggle={handleDeploymentSelection}
                      selectedIds={rawValues.realAssetDeploymentPublicIds}
                    />
                  )}
                </div>
                <div className="relative flex items-center">
                  <label className="relative flex-grow">
                    <input
                      onChange={(e) => {
                        e.preventDefault();
                        const value = e.target.value;
                        if (specifiedSearchType === "Files") {
                          setRawValues({ query: value });
                          setDebouncedQueryParams({ query: value, page: 1 });
                        } else {
                          setRawValues({ sealogEventQuery: value });
                          setDebouncedQueryParams({
                            sealogEventQuery: value,
                            page: 1,
                          });
                        }
                      }}
                      value={
                        specifiedSearchType === "Files"
                          ? rawValues.query
                          : rawValues.sealogEventQuery
                      }
                      type="text"
                      className="form-input block w-full  rounded-md px-3 py-[6px] text-blue-300 hover:shadow-sm focus:shadow-md focus:outline-none focus:ring-0 focus-visible:outline-none"
                      placeholder={
                        specifiedSearchType === "Files"
                          ? "Search Files"
                          : "Search Sealog"
                      }
                    />
                    {(rawValues.query || rawValues.sealogEventQuery) && (
                      <button
                        onClick={() => {
                          if (specifiedSearchType === "Files") {
                            setRawValues({ query: "" });
                            setDebouncedQueryParams({ query: "", page: 1 });
                          } else {
                            setRawValues({ sealogEventQuery: "" });
                            setDebouncedQueryParams({
                              sealogEventQuery: "",
                              page: 1,
                            });
                          }
                        }}
                        className="absolute right-48 top-[3rem] -translate-y-1/2 transform text-gray-400 hover:text-gray-600"
                        style={{ outline: "none" }}
                      ></button>
                    )}
                    {specifiedSearchType === "Files" && searchQueryTooShort && (
                      <p
                        role="status"
                        className="absolute left-0 top-full mt-1 whitespace-nowrap text-sm font-light text-gray-500"
                      >
                        Keep typing — each search term needs at least{" "}
                        {MINIMUM_SEARCH_TOKEN_LENGTH} characters.
                      </p>
                    )}
                  </label>
                  <div className="absolute right-1 top-[19px] z-50 -translate-y-1/2 transform">
                    <SpecifySearchTypeDropdown
                      onChange={
                        handleSpecifiedSearchTypeChange as (
                          value: string
                        ) => void
                      }
                      initialValue={specifiedSearchType}
                      useIcons={true}
                      className="w-[65px]"
                      buttonStyle={"py-[4px]"}
                    />
                  </div>
                </div>
              </div>
            </div>

            <hr className="my-6 border-t-2 border-gray-300" />
            <div className="flex items-start justify-between">
              <div className="mb-4 flex flex-wrap gap-2">
                <FilterChipList filters={filterChips} />
              </div>
              <div className="z-40">
                <SortDropdown
                  label="Asset Created"
                  onChange={handleSortChange}
                  initialSortOrder={queryParams.sortOrder}
                />
              </div>
            </div>

            {searchIsRunnable &&
              (isLoading ? (
                <div className="mt-[7rem]">
                  <LoadingDataAssetCards size={queryParams.size} />
                </div>
              ) : (data?.items?.length ?? 0) > 0 ? (
                data?.items.map((dataAsset, i) => (
                  <DataAssetCard
                    dataAsset={dataAsset}
                    key={`${i}-${dataAsset.path}`}
                    isJustOpened={
                      previewNavigator.lastOpenedAssetId ===
                      (dataAsset.public_id || dataAsset.path)
                    }
                    onPreviewOpen={previewNavigator.openPreview}
                    sealogSearchTerm={queryParams.sealogEventQuery ?? ""}
                    sealogEventValues={queryParams.sealogEventValues}
                    sealogEventFreeTexts={queryParams.sealogEventFreeTexts}
                    sealogEventOptionNames={queryParams.sealogEventOptionNames}
                    sealogEventOptionValues={
                      queryParams.sealogEventOptionValues
                    }
                    regex={queryParams.regex}
                  />
                ))
              ) : (
                <p>No results found.</p>
              ))}
          </div>

          <PaginationFooter
            isPreviousData={isPreviousData}
            page={rawValues.page}
            setPage={(page: number) => setRawValues({ page })}
            hasMore={!!(data && data.items.length !== 0)}
          />
        </>
      </div>
    </>
  );
};

export default RealAssetDeploymentDetailDataAssetList;
